import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Alert } from 'app/models/dto/alert.model';
import { NewNonOwnerPersonnel, NonOwnerPersonnel } from 'app/models/dto/non-owner-personnel.model';
import { NonOwnerPersonnelService } from 'app/services/non-owner-personnel-service/non-owner-personnel.service';
import { ClearNonOwnerPersonnelStore, CreateNonOwnerPersonnel, CreateNonOwnerPersonnelAlert, DeleteNonOwnerPersonnel, DismissNonOwnerPersonnelAlert, GetNonOwnerPersonnelForBorrower, SetNewNonOwnerPersonnel, UnsetNewNonOwnerPersonnel, UpdateNonOwnerPersonnel } from './non-owner-personnel.actions';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';


export class NonOwnerPersonnelStateModel {
    nonOwnerPersonnel: (NonOwnerPersonnel | NewNonOwnerPersonnel)[];
    newNonOwnerPersonnel: NewNonOwnerPersonnel | null;
    loading: boolean;
    processing: {
        saving: boolean;
        nonOwnerPersonnelId?: number | string;
    };
    alerts: Alert[];
}

@State<NonOwnerPersonnelStateModel>({
    name: 'nonOwnerPersonnel',
    defaults: {
        nonOwnerPersonnel: [],
        newNonOwnerPersonnel: null,
        loading: false,
        processing: { saving: false },
        alerts: []
    }
})

@Injectable()

export class NonOwnerPersonnelState {

    @Selector()
    static nonOwnerPersonnel(state: NonOwnerPersonnelStateModel) {
        return state.nonOwnerPersonnel;
    }

    @Selector()
    static newNonOwnerPersonnel(state: NonOwnerPersonnelStateModel) {
        return state.newNonOwnerPersonnel;
    }

    @Selector()
    static loading(state: NonOwnerPersonnelStateModel) {
        return state.loading;
    }

    @Selector()
    static processing(state: NonOwnerPersonnelStateModel) {
        return state.processing;
    }

    @Selector()
    static alerts(state: NonOwnerPersonnelStateModel) {
        return state.alerts;
    }

    constructor(
        private nonOwnerPersonnelService:  NonOwnerPersonnelService,
        private store: Store
    ) {}

    @Action(UnsetNewNonOwnerPersonnel)
    UnsetNewNonOwnerPersonnel({ patchState }: StateContext<NonOwnerPersonnelStateModel>, {}: UnsetNewNonOwnerPersonnel) {
        patchState({ newNonOwnerPersonnel: null });
    }

    @Action(SetNewNonOwnerPersonnel)
    setNewNonOwnerPersonnel({ patchState }: StateContext<NonOwnerPersonnelStateModel>, { emailAddress }: SetNewNonOwnerPersonnel) {
      const newNonOwnerPersonnel = {
        id: null,
        borrowerId: null,
        first: null,
        last: null,
        mobilePhone: null,
        mobilePhoneCountryCodeId: null,
        workPhone: null,
        workPhoneCountryCodeId: null,
        email: emailAddress ?? null,
        title: null
      };
  
      patchState({ newNonOwnerPersonnel });
    }

    @Action(CreateNonOwnerPersonnel)
    createNonOwnerPersonnel(
        { getState, patchState }: StateContext<NonOwnerPersonnelStateModel>,
        newNonOwnerPersonnel: NonOwnerPersonnel
    ) {
        patchState({ processing: { saving: true, nonOwnerPersonnelId: 'new' } });
        return this.nonOwnerPersonnelService.create(newNonOwnerPersonnel).pipe(catchError(err => {
            const errorText = err.message ? err.message : 'Failed to create this new non-owner personnel record. Please try again.';
            this.store.dispatch(new CreateNonOwnerPersonnelAlert('error', errorText));
            patchState({ processing: { saving: false } });
            return of();
        }), tap(nonOwnerPersonnel => {
            patchState({
                nonOwnerPersonnel: [ ...getState().nonOwnerPersonnel.filter(nop => nop.id), nonOwnerPersonnel ],
                newNonOwnerPersonnel: null,
                processing: {saving: false } 
            });
            this.store.dispatch(new CreateNonOwnerPersonnelAlert(
                'success',
                `The record for ${nonOwnerPersonnel.first} was created successfully.`
            ));
        }));
    }

    @Action(GetNonOwnerPersonnelForBorrower)
    getNonOwnerPersonnelForBorrower(
        { getState, patchState }: StateContext<NonOwnerPersonnelStateModel>,
        { borrowerId }: NonOwnerPersonnel
    ) {
        const nopsInState = getState().nonOwnerPersonnel;
        if (
            nopsInState
            && nopsInState.filter(nop => nop.borrowerId && nop.borrowerId === borrowerId).length
        ) { return; }

        // There is a request still pending.
        if (getState().loading) {  return; }

        patchState({ loading: true });
        return this.nonOwnerPersonnelService.getNonOwnerPersonnelForBorrower(borrowerId).pipe(catchError(err => {
            const errorText = err.message ? err.message : 'Failed to get non-owner personnel. Please refresh your page to try again.';
            this.store.dispatch(new CreateNonOwnerPersonnelAlert('error', errorText));
            patchState({ loading: false });
            return of();
        }), tap(nonOwnerPersonnel => {
            patchState({ nonOwnerPersonnel, loading: false });
        }));
    }

    @Action(UpdateNonOwnerPersonnel)
    updateNonOwnerPersonnel(
        { getState, patchState }: StateContext<NonOwnerPersonnelStateModel>,
        nonOwnerPersonnel: NonOwnerPersonnel
    ) {
        patchState({ processing: { saving: true, nonOwnerPersonnelId: nonOwnerPersonnel.id } });
        return this.nonOwnerPersonnelService.update(nonOwnerPersonnel).pipe(catchError(err => {
            const errorText = err.message ? err.message : 'Failed to update this non-owner personnel. Please try again.';
            this.store.dispatch(new CreateNonOwnerPersonnelAlert('error', errorText));
            patchState({ processing: { saving: false } });
          return of();
        }), tap(updatedNonOwnerPersonnel => {
            const nonOwnerPersonnelIndex = getState().nonOwnerPersonnel.findIndex(nop => nop.id === updatedNonOwnerPersonnel.id);
            const nonOwnerPersonnelCopy = [ ...getState().nonOwnerPersonnel ];
            nonOwnerPersonnelCopy[nonOwnerPersonnelIndex] = updatedNonOwnerPersonnel;
            patchState({ nonOwnerPersonnel: nonOwnerPersonnelCopy, processing: { saving: false } });
            this.store.dispatch(new CreateNonOwnerPersonnelAlert(
                'success',
                `The record for ${updatedNonOwnerPersonnel.first} was updated successfully.`
            ));
        }));
    }

    @Action(DeleteNonOwnerPersonnel)
    deleteNonOwnerPersonnel(
        { getState, patchState }: StateContext<NonOwnerPersonnelStateModel>,
        { id }: NonOwnerPersonnel
    ) {
        return this.nonOwnerPersonnelService.delete(id).pipe(catchError(err => {
            const errorText = err.message ? err.message : 'Failed to get non-owner personnel for this borrower. Please try again.';
            this.store.dispatch(new CreateNonOwnerPersonnelAlert('error', errorText));
            throw err;
        }), tap((deletedNonOwnerPersonnel) => {
            patchState({ nonOwnerPersonnel: getState().nonOwnerPersonnel.filter(nop => nop.id !== deletedNonOwnerPersonnel.id) });
            this.store.dispatch(new CreateNonOwnerPersonnelAlert('success', `${deletedNonOwnerPersonnel.first} deleted successfully.`));
        }));
    }

    @Action(CreateNonOwnerPersonnelAlert)
    createNonOwnerPersonnelAlert(
        { getState, patchState }: StateContext<NonOwnerPersonnelStateModel>,
        { alertLevel, message }: CreateNonOwnerPersonnelAlert
    ) {
        const dismissDelay = 10000;
        patchState({ alerts: [ ...getState().alerts, { level: alertLevel, message, dismissDelay } ] });
    }

    @Action(DismissNonOwnerPersonnelAlert)
    dismissNonOwnerPersonnelAlert(
        { getState, patchState }: StateContext<NonOwnerPersonnelStateModel>,
        { alert }: DismissNonOwnerPersonnelAlert
    ) {
        patchState({ alerts: getState().alerts.filter(a => a.message !== alert.message) });
    }

    @Action(ClearNonOwnerPersonnelStore)
    clearNonOwnerPersonnelStore({ patchState }: StateContext<NonOwnerPersonnelStateModel>) {
        patchState({ nonOwnerPersonnel: [] });
    }

}
